import {
  Component,
  Renderer2,
  ViewChild,
  OnDestroy,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';

//
import { AppTopbarComponent } from './app.topbar';
import { AppSidebarComponent } from './app.sidebar';
import { AppFooterComponent } from './app.footer';
import { AppPerBreadcrumbComponent } from './app-per-breadcrumb';

// Services
import { LayoutService } from '../service/layout.service';
import { IRouteInfo, RouteInfoService } from '@/Services/RouteInfo.service';

// PrimeNg
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { LoadingComponent } from '@/shared/components/general/loading/loading.component';
import { chatbotComponent } from '@/pages/test/chatbot/chatbot.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    AppTopbarComponent,
    AppSidebarComponent,
    RouterModule,
    AppFooterComponent,
    BreadcrumbModule,
    AppPerBreadcrumbComponent,
    LoadingComponent,
    chatbotComponent,
  ],
  template: `
    <div class="layout-wrapper flex flex-col h-screen w-screen overflow-hidden" [ngClass]="containerClass">
      <app-loading></app-loading>
      <div class="layout-topbar flex w-full h-[4rem]">
        <app-topbar></app-topbar>
      </div>
      <div class="flex flex-1 flex-row overflow-hidden">
        <div class="layout-sidebar flex-none w-[20rem] m-5">
          <app-sidebar></app-sidebar>
        </div>
        <div class="layout-main-container flex-1 flex flex-col overflow-y-auto">
          <div class="layout-main flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
            <p-breadcrumb class="flex float-left" [model]="items" [home]="home" />
            @if (layoutService.titleSignal()) {
              <div class="text-xl md:text-2xl font-serif font-bold text-gray-400 italic flex items-center gap-2 mr-4">
                <i
                  [ngClass]="layoutService.titleSignal()?.icon"
                  class="text-gray-400 text-[1.3em]"
                ></i>
                <span>{{ layoutService.titleSignal()?.label }}</span>
              </div>
            }
          </div>
          <!-- Zona scrollable de contenido -->
          <div class="flex-1">
            <router-outlet></router-outlet>
          </div>
          <!-- Footer pegado abajo -->
          <app-footer class="mt-auto"></app-footer>
        </div>
      </div>
      <div class="layout-mask animate-fadein"></div>
      <app-chatbot></app-chatbot>
    </div> `,
  })
export class AppLayoutComponent implements OnInit, OnDestroy {
  overlayMenuOpenSubscription: Subscription;
  //esto es para el titulo del lado derecho
  menuOutsideClickListener: any;

  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

  @ViewChild(AppTopbarComponent) appTopBar!: AppTopbarComponent;

  // Services
  layoutService = inject(LayoutService);
  renderer = inject(Renderer2);
  router = inject(Router);

  // Breadcrumb items
  items: MenuItem[] | undefined;
  home: MenuItem | undefined;
  private routeInfoService = inject(RouteInfoService);
  showFloatingWidget = false;
  private routeSubscription?: Subscription;
  routeInfo: IRouteInfo = {
    url: '',
    path: '',
    params: {},
    queryParams: {},
    fragment: null,
    data: {},
    timestamp: '',
  };

  constructor() {
    this.overlayMenuOpenSubscription =
      this.layoutService.overlayOpen$.subscribe(() => {
        if (!this.menuOutsideClickListener) {
          this.menuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              if (this.isOutsideClicked(event)) {
                this.hideMenu();
              }
            }
          );
        }

        if (this.layoutService.layoutState().staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.hideMenu();
      });

    //#region Breadcrumb Configuration
    this.home = { icon: 'pi pi-home', routerLink: '/' };
    //#endregion
  }

  ngOnInit(): void {
    // this.updateRouteInfo();

    // Suscribirse a cambios de ruta
    this.routeSubscription = this.routeInfoService
      .getRouteChanges()
      .subscribe(() => {
        // this.updateRouteInfo();
      });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }

    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }

  isOutsideClicked(event: MouseEvent) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.layout-menu-button');
    const eventTarget = event.target as Node;

    return !(
      sidebarEl?.isSameNode(eventTarget) ||
      sidebarEl?.contains(eventTarget) ||
      topbarEl?.isSameNode(eventTarget) ||
      topbarEl?.contains(eventTarget)
    );
  }

  hideMenu() {
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }
    this.unblockBodyScroll();
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi'
        ),
        ' '
      );
    }
  }

  get containerClass() {
    return {
      'layout-overlay':
        this.layoutService.layoutConfig().menuMode === 'overlay',
      'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
      'layout-static-inactive':
        this.layoutService.layoutState().staticMenuDesktopInactive &&
        this.layoutService.layoutConfig().menuMode === 'static',
      'layout-overlay-active':
        this.layoutService.layoutState().overlayMenuActive,
      'layout-mobile-active':
        this.layoutService.layoutState().staticMenuMobileActive,
    };
  }
}
